import { supabase } from '../lib/supabase';
import { Settings } from '../types';

const BUCKET_NAME = 'logos';

export const settingsService = {
  get: async (): Promise<Settings> => {
    try {
      // Vérifier si des paramètres existent
      const { data: existingSettings, error: checkError } = await supabase
        .from('settings')
        .select('*')
        .limit(1);

      // Si aucun paramètre n'existe, en créer un par défaut
      if (!existingSettings || existingSettings.length === 0) {
        const defaultSettings = { 
          id: 1, 
          logo_url: null,
          is_protected: false,
          pin: null
        };
        const { data: newSettings, error: insertError } = await supabase
          .from('settings')
          .insert([defaultSettings])
          .select()
          .single();

        if (insertError) throw insertError;
        return { 
          logoUrl: null,
          isProtected: false,
          pin: null
        };
      }

      if (checkError) throw checkError;
      return { 
        logoUrl: existingSettings[0].logo_url,
        isProtected: existingSettings[0].is_protected || false,
        pin: existingSettings[0].pin
      };
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      return { 
        logoUrl: null,
        isProtected: false,
        pin: null
      };
    }
  },

  update: async (settings: Partial<Settings>): Promise<Settings> => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .upsert({ 
          id: 1, 
          logo_url: settings.logoUrl,
          is_protected: settings.isProtected,
          pin: settings.pin,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { 
        logoUrl: data.logo_url,
        isProtected: data.is_protected,
        pin: data.pin
      };
    } catch (error: any) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  verifyPin: async (pin: string): Promise<boolean> => {
    try {
      const settings = await settingsService.get();
      return settings.pin === pin;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  },

  setLogo: async (file: File | null): Promise<Settings> => {
    try {
      const currentSettings = await settingsService.get();

      // Supprimer l'ancien logo si nécessaire
      if (currentSettings.logoUrl) {
        const oldPath = currentSettings.logoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from(BUCKET_NAME)
            .remove([oldPath]);
        }
      }

      // Si on veut supprimer le logo
      if (!file) {
        return settingsService.update({ 
          ...currentSettings,
          logoUrl: null 
        });
      }

      // Upload du nouveau logo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(fileName);

      return settingsService.update({ 
        ...currentSettings,
        logoUrl: publicUrl 
      });
    } catch (error: any) {
      console.error('Error updating logo:', error);
      throw error;
    }
  }
};